/**
 * Reads the text layer of a PDF in the browser, page by page, keeping the line breaks the
 * document had (the API's clause splitter needs them). The file is never uploaded: only the
 * text of the pages the user keeps goes anywhere.
 */

import { findSheetNumber, type DrawingPage, type RawPage, type TextItem } from "./speclens-reader.ts";

interface PageLike {
  getTextContent(): Promise<{ items: readonly unknown[] }>;
  cleanup(): void;
}

interface DocumentLike {
  readonly numPages: number;
  getPage(pageNumber: number): Promise<PageLike>;
}

/** The slice of pdf.js used here, so a test can pass the Node build and the app the browser one. */
export interface PdfjsLike {
  getDocument(source: { data: Uint8Array; useWorkerFetch?: boolean; disableFontFace?: boolean }): {
    readonly promise: Promise<DocumentLike>;
    destroy(): Promise<void>;
  };
}

export interface ReadOptions {
  /** Also find each page's sheet number (a drawing set). */
  readonly sheets: boolean;
  readonly onProgress?: (done: number, total: number) => void;
}

interface PdfTextItem {
  readonly str: string;
  readonly hasEOL?: boolean;
  readonly height?: number;
}

function isTextItem(item: unknown): item is PdfTextItem {
  return typeof item === "object" && item !== null && "str" in item;
}

export async function readPdfPages(pdfjs: PdfjsLike, data: Uint8Array, options: ReadOptions): Promise<readonly DrawingPage[]> {
  const task = pdfjs.getDocument({ data, useWorkerFetch: false, disableFontFace: true });
  const document = await task.promise;
  const pages: DrawingPage[] = [];
  try {
    for (let number = 1; number <= document.numPages; number++) {
      const page = await document.getPage(number);
      try {
        const content = await page.getTextContent();
        let text = "";
        const sized: TextItem[] = [];
        for (const item of content.items) {
          if (!isTextItem(item)) continue;
          text += item.str + (item.hasEOL === true ? "\n" : " ");
          if (options.sheets) sized.push({ text: item.str, size: item.height ?? 0 });
        }
        const sheet = options.sheets ? findSheetNumber(sized) : undefined;
        pages.push({ page: number, text: text.trimEnd(), ...(sheet !== undefined && { sheet }) });
      } finally {
        page.cleanup();
      }
      options.onProgress?.(number, document.numPages);
    }
  } finally {
    await task.destroy();
  }
  return pages;
}

export function asRawPages(pages: readonly DrawingPage[]): readonly RawPage[] {
  return pages.map(({ page, text }) => ({ page, text }));
}

let browserPdfjs: PdfjsLike | null = null;

async function loadBrowserPdfjs(): Promise<PdfjsLike> {
  if (browserPdfjs !== null) return browserPdfjs;
  const [module, { default: workerUrl }] = await Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
  ]);
  module.GlobalWorkerOptions.workerSrc = workerUrl;
  browserPdfjs = module as unknown as PdfjsLike;
  return browserPdfjs;
}

export async function readPdfFile(file: File, options: ReadOptions): Promise<readonly DrawingPage[]> {
  const pdfjs = await loadBrowserPdfjs();
  return readPdfPages(pdfjs, new Uint8Array(await file.arrayBuffer()), options);
}

/** A spec may also arrive as plain text: one page, no section code, so it is analysed whole. */
export async function readTextFile(file: File): Promise<readonly DrawingPage[]> {
  return [{ page: 1, text: await file.text() }];
}
